import { useState } from 'react'
import { Box, AppBar, Toolbar, IconButton, Menu, MenuItem, Avatar, Typography, Tooltip, ListItemIcon, ListItemText, Divider } from '@mui/material'
import { Logout, Person, DarkMode, LightMode } from '@mui/icons-material'
import { useAuthStore } from '@store/authStore'
import { useUIStore } from '@store/uiStore'
import { useAuth } from '@hooks/useAuth'
import { useTranslation } from '@i18n'
import LanguageSwitcher from '@components/Common/LanguageSwitcher'

const ScannerLayout = ({ children }) => {
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const { logout } = useAuth()
    const { darkMode, toggleDarkMode, themeSettings } = useUIStore()
    const [anchorEl, setAnchorEl] = useState(null)

    const isRTL = language === 'ar'
    const open = Boolean(anchorEl)

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const handleLogout = () => {
        handleMenuClose()
        logout()
    }

    const getUserName = () => {
        if (language === 'ar' && user?.name_ar) {
            return user.name_ar
        }
        return user?.name_en || user?.email || 'User'
    }

    const getInitials = () => {
        const name = getUserName()
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2)
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* Header */}
            <AppBar
                position="static"
                sx={{
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: 1,
                }}
            >
                <Toolbar>
                    {/* Logo */}
                    <Box
                        component="img"
                        src={themeSettings?.logoUrl || '/moj-logo.png'}
                        alt="Logo"
                        sx={{
                            height: 36,
                            width: 'auto',
                            mr: isRTL ? 0 : 2,
                            ml: isRTL ? 2 : 0,
                        }}
                    />

                    {/* Title with gradient */}
                    <Typography
                        variant="h6"
                        sx={{
                            flexGrow: 1,
                            background: 'linear-gradient(135deg, #1a3a6b 0%, #0d2247 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontWeight: 700,
                        }}
                    >
                        {isRTL ? 'ماسح رمز التحقق' : 'QR Scanner'}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Theme Toggle */}
                        <Tooltip title={darkMode ? t('settings.lightMode') : t('settings.darkMode')}>
                            <IconButton onClick={toggleDarkMode} color="inherit">
                                {darkMode ? <LightMode /> : <DarkMode />}
                            </IconButton>
                        </Tooltip>

                        {/* Language Switcher */}
                        <LanguageSwitcher />

                        {/* Profile Menu */}
                        <Tooltip title={getUserName()}>
                            <IconButton
                                onClick={handleMenuOpen}
                                size="small"
                                sx={{ ml: 1 }}
                            >
                                <Avatar
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: 'primary.main',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    {getInitials()}
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        PaperProps={{
                            sx: { minWidth: 200, mt: 1 },
                        }}
                    >
                        {/* User Info */}
                        <Box sx={{ px: 2, py: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {getUserName()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {user?.email}
                            </Typography>
                            <Typography variant="caption" color="primary.main">
                                {isRTL ? 'ماسح QR' : 'QR Scanner'}
                            </Typography>
                        </Box>

                        <Divider />

                        {/* Logout */}
                        <MenuItem onClick={handleLogout}>
                            <ListItemIcon>
                                <Logout fontSize="small" color="error" />
                            </ListItemIcon>
                            <ListItemText sx={{ color: 'error.main' }}>
                                {t('auth.logout')}
                            </ListItemText>
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            {/* Main Content */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {children}
            </Box>
        </Box>
    )
}

export default ScannerLayout
